import React, { useEffect, useState } from "react";
import "./style.css";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { IMAGES } from "@/assets/IMAGES";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/InputOtp";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { OTPFormValues, otpSchema } from "@/schema/LoginSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/components/ui/UseToast";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/app/store";
import { login, loginOtp } from "@/features/auth/AuthSlice";
import useDocumentTitle from "@/hooks/UseDocumentTitle";
import { extractLinks, getLinks } from "@/utils/Helper";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/MobileToolTip";
import { Button } from "@/components/ui/Button";
const { VITE_IS_PRODUCTION, VITE_IS_LOCAL, VITE_LOCAL_URL } = import.meta.env;
const LoginOTP: React.FC = () => {
  useDocumentTitle("Verify your OTP");
  const links = getLinks() || ["/"]; // Return landing page as default;
  const isAuthenticated = Boolean(localStorage.getItem("userToken"));
  const location = useLocation();
  const userPersona = localStorage.getItem("userType");
  const [isVerifying, setIsVerifying] = useState(false); // Add state for verifying OTP

  useEffect(() => {
    if (
      isAuthenticated &&
      userPersona === "Admin" &&
      location.pathname === "/login-otp"
    ) {
      navigate(links[0]);
    }
  }, [location]);

  const { toast } = useToast();
  const { email, emailResponse } = location.state || {};
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [remainingTime, setRemainingTime] = useState<number>(
    emailResponse?.otp_expiry_seconds || 0
  );

  // Format the remaining time to MM:SS format
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const {
    control,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
    reset,
  } = useForm<OTPFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: { otp: "" },
  });
  const isEmailinLocal = localStorage.getItem("email") ?? "";

  const onSubmit = (data: OTPFormValues) => {
    setIsVerifying(true);

    if (data.otp) {
      dispatch(
        loginOtp({
          email,
          otp: data.otp,
          fcm_token: "",
          its_id: "",
          domain:
            VITE_IS_LOCAL === "true"
              ? VITE_LOCAL_URL
              : VITE_IS_PRODUCTION === "true"
                ? window.location.hostname
                : window.location.host,
        })
      )
        .unwrap()
        .then((response) => {
          toast({
            variant: "success",
            description: (
              <div className="flex items-center space-x-2 w-full h-10">
                <img
                  src={IMAGES.Status_icon}
                  alt="Success"
                  className="w-6 h-6"
                />
                <span>{response.message}</span>
              </div>
            ),
          });

          const sidepanel = localStorage.getItem("sidePanel");
          const decodedSidepanel = JSON.parse(atob(sidepanel as string));
          const links = extractLinks(decodedSidepanel);
          navigate(links[0]);
        })
        .catch((error) => {
          toast({
            variant: "destructive",
            description: error,
          });
        })
        .finally(() => {
          setIsVerifying(false);
        });
    } else {
    }
  };

  const onResentOtp = async () => {
    dispatch(
      login({
        email,
        rememberme: isEmailinLocal !== "",
        domain:
          VITE_IS_LOCAL === "true"
            ? VITE_LOCAL_URL
            : VITE_IS_PRODUCTION === "true"
              ? window.location.hostname
              : window.location.host,
      })
    )
      .unwrap()
      .then(({ message, ...response }) => {
        toast({
          variant: "success",
          description: (
            <div className="flex items-center space-x-2 w-full h-10">
              <img src={IMAGES.Status_icon} alt="Success" className="w-6 h-6" />
              <span>{message}</span>
            </div>
          ),
        });

        setRemainingTime(emailResponse?.otp_expiry_seconds);
        reset({ otp: "" }); // Reset the OTP field
      })
      .catch((error) => {
        toast({
          variant: "destructive",
          description: error,
        });
      });
  };

  // Watch the OTP input and trigger submit when fully filled
  // Watch OTP field
  const otp = watch("otp");
  useEffect(() => {
    if (otp && otp.length === 6) {
      trigger().then((isValid) => {
        if (isValid) {
          handleSubmit(onSubmit)();
        }
      });
    }
  }, [otp, trigger, handleSubmit]);

  useEffect(() => {
    // Start countdown timer when otp_expiry_seconds is available
    if (remainingTime > 0) {
      const timer = setInterval(() => {
        setRemainingTime((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
      }, 1000);

      // Clear interval when the component is unmounted or remainingTime reaches 0
      return () => clearInterval(timer);
    }
  }, [remainingTime]);

  return (
    <div className="loginpage-image  ">
      <div className="max-w-[1800px] mx-auto xs:mx-0">
        <div className="flex sm:mx-16 justify-center sm:justify-between items-center h-dvh xs:mx-0">
          <div className=" flex flex-col gap-2 xs:gap-0"></div>
          <div>
            <Card className="px-16 max-w-md bg-transparent bg-opacity-10 backdrop-blur-sm custom-gradient-bg rounded-3xl border-checkboxborder shadow-lg p-2">
              <CardHeader>
                <CardTitle>
                  <div className="gap-1 flex justify-center items-center">
                    <div>
                      <img
                        src={IMAGES.logo_icon}
                        height={110}
                        width={100}
                        alt="Main logo"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <p className="text-primary leading-5 italic font-semibold text-[1.8rem]">
                        Enter Code
                      </p>
                      <p className="text-textgray leading-5 text-[0.9rem]">
                        We sent OTP code to your email address
                      </p>
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
                  <div className="flex my-3 flex-col justify-center items-center">
                    <Controller
                      name="otp"
                      control={control}
                      render={({ field }) => (
                        <InputOTP
                          maxLength={6}
                          {...field}
                          onInput={(e) => {
                            // Remove non-numeric characters
                            e.currentTarget.value =
                              e.currentTarget.value.replace(/\D/g, "");
                          }}
                        >
                          <InputOTPGroup>
                            {[...Array(6)].map((_, index) => (
                              <InputOTPSlot key={index} index={index} />
                            ))}
                          </InputOTPGroup>
                        </InputOTP>
                      )}
                    />
                    {errors.otp && (
                      <p className="text-red-500 text-sm mt-2 w-full px-14">
                        {errors.otp.message}
                      </p>
                    )}
                    <div className="flex justify-between items-center w-full mt-3 ">
                      <p className="text-[.8rem] text-primary">
                        Remaining time:{" "}
                        <span className="text-textgray">
                          {formatTime(remainingTime)}
                        </span>
                      </p>
                      <TooltipProvider>
                        {remainingTime < 90 ? (
                          <button
                            onClick={onResentOtp}
                            disabled={remainingTime >= 90}
                            className="disabled:cursor-not-allowed text-[.8rem] text-primary disabled:text-gray-400 underline hover:cursor-pointer"
                          >
                            Resend OTP
                          </button>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger
                              asChild
                              className="hover:cursor-pointer"
                            >
                              <button
                                onClick={onResentOtp}
                                disabled={remainingTime >= 90}
                                className="disabled:cursor-not-allowed text-[.8rem] text-primary disabled:text-gray-400 underline hover:cursor-pointer"
                              >
                                Resend OTP
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-52">
                              You can request a new OTP 30 seconds after the
                              last OTP request.
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                    </div>
                  </div>
                  {/* <CommonButton title="Continue" type="submit" /> */}

                  <Button
                    type="submit"
                    disabled={isVerifying}
                    className="w-full text-black  hover:text-white hover:bg-primary"
                  >
                    {isVerifying ? "Verifying OTP" : "Verify OTP & Continue"}
                    {isVerifying && (
                      <div className="w-5 h-5 ml-2 border-2 border-gray-300 border-t-primary rounded-full animate-spin"></div>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginOTP;
